import type { LucideIcon } from "lucide-react";
import {
  Activity,
  BarChart3,
  Clapperboard,
  CreditCard,
  Crown,
  DollarSign,
  Flame,
  Gift,
  Globe2,
  HeartHandshake,
  HeartPulse,
  Image,
  Layout,
  LayoutDashboard,
  Layers,
  Link2,
  Lock,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  MessageSquare,
  MousePointerClick,
  Package,
  Repeat2,
  Route,
  Scale,
  ServerCog,
  Settings2,
  ShieldCheck,
  Sparkles,
  Target,
  Trophy,
  Tv2,
  Upload,
  UserSearch,
  Users,
  Wallet,
  Zap,
} from "lucide-react";

export interface AdminSidebarNavChild {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
}

/** Entries shown in AdminLayout — must match nested routes under ProtectedAdminRoute in App.tsx */
export interface AdminSidebarNavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
  /** When true, only exact path matches for active state (e.g. /admin vs /admin/…) */
  readonly end: boolean;
  /** Optional one-line description (used in the ⌘K command palette and header hint) */
  readonly hint?: string;
  /** Sub-items rendered as an expandable group */
  readonly children?: readonly AdminSidebarNavChild[];
  /** Optional live count / badge shown next to label (e.g. pending approvals) */
  readonly badge?: number | string | null;
}

/** A labelled section of the sidebar (IA orientada a operación diaria) */
export interface AdminSidebarNavGroup {
  readonly label: string;
  readonly items: readonly AdminSidebarNavItem[];
}

/**
 * Orden de gestión (ops-first, sin pisar funciones):
 *  1. Operación — colas del día (membresías ≠ abandonos checkout ≠ lifecycle)
 *  2. Métricas  — lectura; conversión anidada bajo Analytics
 *  3. Contenido — sitio / portfolio / DualCTA / premium (1 entrada por job)
 *  4. Geo       — CTA fino + template por país (única entrada) + tráfico
 *  5. Pagos     — planes / MP / Stripe / canal
 *  6. Sistema   — salud, super-only, setup
 *
 * No duplicar country-template ni listar Mux conversión 2 veces.
 */
export const ADMIN_SIDEBAR_NAV_GROUPS: readonly AdminSidebarNavGroup[] = [
  {
    label: "Operación",
    items: [
      {
        to: "/admin",
        label: "Dashboard",
        icon: LayoutDashboard,
        end: true,
        hint: "Hoy: pendientes, métricas y accesos",
      },
      {
        to: "/admin/memberships",
        label: "Aprobar membresías",
        icon: Crown,
        end: false,
        hint: "Cola de pagos VIP — Telegram y Clerk",
      },
      {
        to: "/admin/free-members",
        label: "Miembros free",
        icon: Users,
        end: false,
        hint: "Registro gratuito pendiente de aprobación",
      },
      {
        to: "/admin/active-members",
        label: "Directorio miembros",
        icon: UserSearch,
        end: false,
        hint: "Buscar, extender, suspender",
      },
      {
        to: "/admin/member-lifecycle-recovery",
        label: "Ciclo de vida",
        icon: Repeat2,
        end: false,
        hint: "Por vencer, past_due, expirados — no es abandono de checkout",
      },
      {
        to: "/admin/recovery",
        label: "Abandonos checkout",
        icon: HeartHandshake,
        end: false,
        hint: "Carritos Stripe/MP a mitad de compra (no es lifecycle)",
      },
      {
        to: "/admin/campaigns/argentina-win",
        label: "Campaña Argentina 85%",
        icon: Trophy,
        end: false,
        hint: "Festejo + exclusivos · checkout con descuento · envío manual",
      },
      {
        to: "/admin/email-broadcast",
        label: "Email broadcast",
        icon: Mail,
        end: false,
        hint: "Envío masivo segmentado",
      },
    ],
  },
  {
    label: "Métricas",
    items: [
      {
        to: "/admin/business-pulse",
        label: "Business Pulse",
        icon: HeartPulse,
        end: false,
        hint: "Resumen diario: revenue + pipeline + churn",
      },
      {
        to: "/admin/revenue",
        label: "Revenue",
        icon: DollarSign,
        end: false,
        hint: "Detalle de ingresos Stripe + MercadoPago",
      },
      {
        to: "/admin/analytics",
        label: "Analytics",
        icon: BarChart3,
        end: false,
        hint: "Embudo PostHog y desgloses",
        children: [
          {
            to: "/admin/web-templates",
            label: "Templates por país",
            icon: Globe2,
          },
          { to: "/admin/fotos-analytics", label: "Imágenes", icon: Image },
          {
            to: "/admin/mux-analytics",
            label: "Videos · esta brand",
            icon: Tv2,
          },
          {
            to: "/admin/mux-conversion",
            label: "Mux · a checkout",
            icon: MousePointerClick,
          },
          {
            to: "/admin/conversion-health",
            label: "Salud embudo 24h",
            icon: Activity,
          },
        ],
      },
    ],
  },
  {
    label: "Contenido",
    items: [
      {
        to: "/admin/site-settings",
        label: "Sitio y hero",
        icon: Layout,
        end: false,
        hint: "Logo, banner, galería, footer",
      },
      {
        to: "/admin/portfolio",
        label: "Portfolio",
        icon: Image,
        end: false,
        hint: "Galería por sección",
      },
      {
        to: "/admin/mediakit",
        label: "Media Kit",
        icon: Layout,
        end: false,
        hint: "Presentación comercial /mediakit",
      },
      {
        to: "/admin/audiovisual",
        label: "YouTube / audiovisual",
        icon: Tv2,
        end: false,
        hint: "Videos YouTube de la sección audiovisual del sitio",
      },
      {
        to: "/admin/dual-cta-home",
        label: "Home DualCTA",
        icon: Target,
        end: false,
        hint: "Video Mux de la home y conversión (no edita CTA por provincia)",
        children: [
          {
            to: "/admin/dual-cta-templates",
            label: "Templates DualCTA",
            icon: Tv2,
          },
        ],
      },
      {
        to: "/admin/comunidad",
        label: "Comunidad",
        icon: Sparkles,
        end: false,
        hint: "AR: CTA Comunidad → /invitacion-premium · Mux + MercadoPago",
        children: [
          { to: "/admin/comunidad#mux", label: "Mux playback", icon: Clapperboard },
          { to: "/admin/comunidad#checkout", label: "Checkout / MP", icon: CreditCard },
          { to: "/admin/cta-por-region", label: "Quién ve Comunidad", icon: MapPin },
          {
            to: "/admin/premium-invite-config",
            label: "Config avanzada",
            icon: Layers,
          },
        ],
      },
      {
        to: "/admin/premium-invite-config",
        label: "Premium invite",
        icon: Layers,
        end: false,
        hint: "Copy, A/B layout, cards del embudo premium",
        children: [
          { to: "/admin/premium-invite-config#cards", label: "Cards Bio", icon: MessageCircle },
          { to: "/admin/premium-invite-config#embudo", label: "Embudo & Texto", icon: Layers },
        ],
      },
      {
        to: "/admin/invitaciones",
        label: "Invitaciones",
        icon: Mail,
        end: false,
        hint: "Códigos de acceso restringido",
      },
      {
        to: "/dashboard/crear-producto",
        label: "Productos one-shot",
        icon: Package,
        end: false,
        hint: "Ventas únicas (no suscripciones)",
      },
    ],
  },
  /**
   * Reels is its own product surface, not a sub-topic of Contenido: it owns a
   * public route (/reels), its own Mux feed, its own link types and expiries.
   *
   * Flat on purpose — every screen is one click, nothing hides behind a
   * disclosure, and growth means appending a row here rather than nesting
   * deeper. `end` on the overview so it stops being active on sub-routes.
   */
  {
    label: "Reels",
    items: [
      {
        to: "/admin/reels",
        label: "Resumen",
        icon: Clapperboard,
        end: true,
        hint: "Estado del feed, links por vencer y accesos rápidos",
      },
      {
        to: "/admin/reels/share-links",
        label: "Clips / share links",
        icon: Link2,
        end: false,
        hint: "Playlist propia + expire por link, de a uno o en lote",
      },
      {
        to: "/admin/reels/upload",
        label: "Upload Mux",
        icon: Upload,
        end: false,
        hint: "Direct Upload al feed del brand + biblioteca",
      },
      {
        to: "/admin/reels/secret-share",
        label: "Secret share",
        icon: Flame,
        end: false,
        hint: "/reels?t= con máx. aberturas y panel de tracking",
      },
      {
        to: "/admin/reels/feed",
        label: "Feed & config",
        icon: Settings2,
        end: false,
        hint: "Fuente del catálogo, password full y TTL por defecto",
      },
    ],
  },
  {
    label: "Geo",
    items: [
      {
        to: "/admin/visitor-experience",
        label: "Live experiencia",
        icon: Activity,
        end: false,
        hint: "Qué se muestra ahora por geo · deep-links a editores",
        children: [
          {
            to: "/admin/visitor-experience/funnels",
            label: "Intentos DualCTA",
            icon: Route,
          },
        ],
      },
      {
        to: "/admin/country-template",
        label: "Experiencia país",
        icon: Globe2,
        end: false,
        hint: "Template y video por mercado",
      },
      {
        to: "/admin/cta-por-region",
        label: "Argentina CTA",
        icon: MapPin,
        end: false,
        hint: "Hero y MercadoPago por provincia / ciudad / barrio",
      },
      {
        to: "/admin/geo-traffic",
        label: "Demanda geo",
        icon: Map,
        end: false,
        hint: "Tráfico por ubicación · oportunidades de volumen",
      },
    ],
  },
  {
    label: "Pagos",
    items: [
      {
        to: "/dashboard/subscripciones/planes",
        label: "Editar planes",
        icon: Repeat2,
        end: false,
        hint: "Crear/editar precios Stripe y MercadoPago",
      },
      {
        to: "/admin/mercadopago-plans",
        label: "MP operativo (ARS)",
        icon: CreditCard,
        end: false,
        hint: "Planes AR, suscriptores, comprobantes",
      },
      {
        to: "/admin/stripe-payments-config",
        label: "Config Stripe",
        icon: CreditCard,
        end: false,
        hint: "Moneda, países, adaptive pricing",
      },
      {
        to: "/admin/sorprendeme-config",
        label: "Sorprendeme montos",
        icon: Gift,
        end: false,
        hint: "Chips ARS/USD + mínimo /sorprendeme",
      },
      {
        to: "/admin/stripe-disputes",
        label: "Disputas Stripe",
        icon: Scale,
        end: false,
        hint: "Contracargos y evidencia",
      },
      {
        to: "/admin/whatsapp-broadcast-config",
        label: "Canal WhatsApp",
        icon: MessageSquare,
        end: false,
        hint: "Precio del canal de difusión (producto)",
      },
    ],
  },
  {
    label: "Sistema",
    items: [
      {
        to: "/admin/config-health",
        label: "Config Health",
        icon: ShieldCheck,
        end: false,
        hint: "Env vars e integraciones",
      },
      {
        to: "/admin/notifications-health",
        label: "Email / notificaciones",
        icon: Mail,
        end: false,
        hint: "Resend y tests de ingreso",
      },
      {
        to: "/admin/worker-flags",
        label: "Worker / Edge",
        icon: Zap,
        end: false,
        hint: "Feature flags Cloudflare",
      },
      {
        to: "/admin/iplocate-health",
        label: "IPLocate",
        icon: Activity,
        end: false,
        hint: "Quota y errores de geo",
      },
      {
        to: "/admin/audit-log",
        label: "Audit log",
        icon: Activity,
        end: false,
        hint: "Historial de acciones admin",
      },
      {
        to: "/admin/finanzas-personales",
        label: "Finanzas Delfina",
        icon: Wallet,
        end: false,
        hint: "Solo super admin · finanzas personales + feed marca",
      },
      {
        to: "/admin/access-control",
        label: "Control de acceso",
        icon: Lock,
        end: false,
        hint: "Solo super admin · qué ve cada operador",
      },
      {
        to: "/admin/investor-kpi",
        label: "KPIs Investors",
        icon: Target,
        end: false,
        hint: "Solo super admin · MRR / North Star",
      },
      {
        to: "/dashboard/railway-setup",
        label: "Setup Railway",
        icon: ServerCog,
        end: false,
        hint: "SPA, API, crons, Postgres",
      },
      {
        to: "/dashboard/cloudflare-setup",
        label: "Setup Cloudflare",
        icon: ServerCog,
        end: false,
        hint: "Worker geo + proxy",
      },
    ],
  },
];

/** Flat list (backward-compatible export) — derived from the grouped structure. */
export const ADMIN_SIDEBAR_NAV_ITEMS: readonly AdminSidebarNavItem[] =
  ADMIN_SIDEBAR_NAV_GROUPS.flatMap((g) => g.items);

// ── Visibilidad de features por rol ───────────────────────────────────────────

export const ACCESS_CONTROL_PATH = "/admin/access-control";

/**
 * Landing del admin regular al entrar al panel (siempre accesible).
 * Finanzas personales de la creadora NO es principal: es super-admin-only
 * (API + página + nav) por datos sensibles + feed Stripe/MP de la marca.
 */
export const PRINCIPAL_ADMIN_PATH = "/admin";

/**
 * Rutas que SIEMPRE son super-admin-only (no se togglean: el admin regular nunca
 * las ve ni puede entrar). Centralizado acá para que AdminLayout (filtro + guard)
 * y la página de control compartan la misma fuente.
 */
export const SUPER_ADMIN_ONLY_PATHS: ReadonlySet<string> = new Set([
  "/admin/investor-kpi",
  ACCESS_CONTROL_PATH,
  // Finanzas personales Delfina + ingresos reales: super-admin only
  // (api/admin/finanzas-personales + FinanzasPersonalesAdmin).
  "/admin/finanzas-personales",
]);

/** Home del panel — nunca ocultable. */
const NON_TOGGLEABLE_PATHS: ReadonlySet<string> = new Set(["/admin"]);

/** Normaliza un `to` a su feature_key (sin hash ni query). */
export function toFeatureKey(to: string): string {
  return to.split("#")[0].split("?")[0];
}

export interface ToggleableAdminFeature {
  readonly key: string;
  readonly label: string;
  readonly group: string;
  /** Descripción one-line (del sidebar) — para que la página de control se entienda. */
  readonly hint?: string;
}

/**
 * Lista de features que el super admin puede ocultar — derivada del sidebar para
 * mantenerse en sync. Excluye la home y las super-admin-only.
 */
export function getToggleableAdminFeatures(): ToggleableAdminFeature[] {
  const out: ToggleableAdminFeature[] = [];
  const seen = new Set<string>();
  for (const group of ADMIN_SIDEBAR_NAV_GROUPS) {
    for (const item of group.items) {
      const key = toFeatureKey(item.to);
      if (NON_TOGGLEABLE_PATHS.has(key) || SUPER_ADMIN_ONLY_PATHS.has(key)) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ key, label: item.label, group: group.label, hint: item.hint });
    }
  }
  return out;
}

/**
 * Features que SIEMPRE son super-admin-only (informativo para la página de
 * control: el admin regular nunca las ve, no se togglean).
 */
export function getSuperAdminOnlyFeatures(): ToggleableAdminFeature[] {
  const out: ToggleableAdminFeature[] = [];
  const seen = new Set<string>();
  for (const group of ADMIN_SIDEBAR_NAV_GROUPS) {
    for (const item of group.items) {
      const key = toFeatureKey(item.to);
      if (!SUPER_ADMIN_ONLY_PATHS.has(key) || seen.has(key)) continue;
      seen.add(key);
      out.push({ key, label: item.label, group: group.label, hint: item.hint });
    }
  }
  return out;
}

/**
 * Feature keys que tienen entrada propia de primer nivel en el sidebar.
 * Se deriva de los grupos, así que agregar un item lo registra solo.
 */
const TOP_LEVEL_FEATURE_KEYS: ReadonlySet<string> = new Set(
  ADMIN_SIDEBAR_NAV_GROUPS.flatMap((group) =>
    group.items.map((item) => toFeatureKey(item.to)),
  ),
);

/**
 * ¿La ruta actual está bloqueada para un admin regular?
 * (oculta por toggle o super-admin-only).
 *
 * Cuidado con el matcheo: no es prefijo a secas. Reels cuelga sus cinco
 * pantallas de /admin/reels pero en la nav son hermanas, cada una con su
 * toggle. Con prefijo puro, ocultar "Resumen" se llevaba puestas a las otras
 * cuatro y los toggles no eran independientes de verdad.
 *
 * Regla: si la ruta tiene entrada propia en el sidebar, manda sólo SU toggle.
 * Cualquier otra cosa (sub-rutas sueltas, hijos con hash) hereda del ancestro.
 * Las super-admin-only siguen bloqueando todo su subárbol, sin excepción.
 */
export function isAdminPathBlocked(
  pathname: string,
  hiddenSet: ReadonlySet<string>,
): boolean {
  const path = toFeatureKey(pathname);

  for (const key of SUPER_ADMIN_ONLY_PATHS) {
    if (path === key || path.startsWith(`${key}/`)) return true;
  }

  if (TOP_LEVEL_FEATURE_KEYS.has(path)) return hiddenSet.has(path);

  for (const key of hiddenSet) {
    if (path === key || path.startsWith(`${key}/`)) return true;
  }
  return false;
}
